import DefaultIcon from '@/assets/images/default icon.png';

import { getProviderImagePath, transportProviderTitles } from './imageUtils';

export type ProviderMode = 'air' | 'ocean' | 'road';

const norm = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const CORPORATE_WORDS = new Set([
  'air',
  'airline',
  'airlines',
  'airways',
  'co',
  'corp',
  'express',
  'freight',
  'group',
  'inc',
  'line',
  'lines',
  'llc',
  'logistics',
  'ltd',
  'the',
]);

/** Name without its corporate words, so "Delta" and "Delta Airlines" meet. */
const coreOf = (name: string): string =>
  norm(name)
    .split(' ')
    .filter((word) => word && !CORPORATE_WORDS.has(word))
    .join(' ');

/** Airline IATA codes and names that differ from the logo's file title; air legs only. */
const AIR_ALIAS_TO_TITLE: Record<string, string> = {
  aa: 'American Airlines',
  as: 'Alaska Airlines',
  ac: 'Air Canada',
  af: 'Air France KLM',
  'air france': 'Air France KLM',
  b6: 'JetBlue',
  ba: 'British Airways',
  ca: 'Air China',
  cx: 'Cathay',
  'cathay pacific': 'Cathay',
  dl: 'Delta Airlines',
  ek: 'Emirates',
  ey: 'Etihad',
  ib: 'Iberia',
  jl: 'Japan Airlines',
  ke: 'Korean Air',
  kl: 'Air France KLM',
  lh: 'Lufthansa',
  nk: 'Spirit Airlines',
  'lufthansa cargo': 'Lufthansa',
  qr: 'Qatar Airways',
  sq: 'Singapore Airlines',
  tk: 'Turkish Airlines',
  ua: 'United Airlines',
  wn: 'Southwest Airlines',
};

/** Carrier names that differ from the logo's file title on any mode. */
const OTHER_ALIAS_TO_TITLE: Record<string, string> = {
  cosco: 'COSCO Shipping Lines',
  'ocean network express': 'ONE',
  'hyundai merchant marine': 'HMM',
};

/** Logo titles that are airlines: their core names only match on air legs. */
const AIRLINE_TITLES = new Set([
  'Air Belgium',
  'Alaska Airlines',
  'Air Canada',
  'Air China',
  'Air Europa',
  'Air France KLM',
  'All Airlines',
  'American Airlines',
  'British Airways',
  'Cathay',
  'Delta Airlines',
  'Emirates',
  'Etihad',
  'Finnair',
  'Iberia',
  'Japan Airlines',
  'JetBlue',
  'Korean Air',
  'Lufthansa',
  'Qatar Airways',
  'Singapore Airlines',
  'Southwest Airlines',
  'Spirit Airlines',
  'Swiss',
  'Turkish Airlines',
  'United Airlines',
]);

type LogoIndex = {
  exact: Map<string, string>;
  core: Map<string, string>;
  airlineCore: Map<string, string>;
};

let logoIndex: LogoIndex | null = null;

const buildIndex = (titles: string[]): LogoIndex => {
  const index: LogoIndex = {
    exact: new Map(),
    core: new Map(),
    airlineCore: new Map(),
  };
  titles.forEach((title) => {
    if (!index.exact.has(norm(title))) index.exact.set(norm(title), title);
    const core = coreOf(title);
    if (!core) return;
    const byCore = AIRLINE_TITLES.has(title) ? index.airlineCore : index.core;
    if (!byCore.has(core)) byCore.set(core, title);
  });
  return index;
};

/** The bundled logo title a carrier name points at: exact title, then alias, then core name. */
const logoTitleFor = (name: string, mode?: ProviderMode | null): string => {
  logoIndex ??= buildIndex(transportProviderTitles());
  const key = norm(name);
  const exact = logoIndex.exact.get(key);
  if (exact) return exact;
  const alias =
    (mode === 'air' ? AIR_ALIAS_TO_TITLE[key] : undefined) ??
    OTHER_ALIAS_TO_TITLE[key];
  if (alias) return alias;
  const core = coreOf(name);
  return (
    (mode === 'air' ? logoIndex.airlineCore.get(core) : undefined) ??
    logoIndex.core.get(core) ??
    name
  );
};

/** The bundled logos never change, and a miss scans them all: remember each answer. */
const logoByTitle = new Map<string, string | null>();

/**
 * The logo the product already has for a courier or carrier, found by its title,
 * an IATA code on an air leg, or its name without corporate words. Null when there
 * is none, so the caller can show an initials badge instead of the generic mode icon.
 */
export function providerLogoFor(
  name?: string | null,
  mode?: ProviderMode | null
): string | null {
  const title = (name ?? '').trim();
  if (!title) return null;
  const cacheKey = `${mode ?? ''}|${title}`;
  let logo = logoByTitle.get(cacheKey);
  if (logo === undefined) {
    const found = getProviderImagePath(
      logoTitleFor(title, mode),
      'transportProvider'
    );
    logo = found === DefaultIcon ? null : found;
    logoByTitle.set(cacheKey, logo);
  }
  return logo;
}

/** Two letters for a carrier with no logo: its first two words, or the first two letters of one word. */
export function carrierInitials(name?: string | null): string {
  const words = norm(name ?? '')
    .split(' ')
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
